import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { fileUrl } from "@/lib/api";

const PlayerContext = createContext(null);

export const usePlayer = () => useContext(PlayerContext);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  if (!audioRef.current) {
    audioRef.current = new Audio();
    audioRef.current.preload = "metadata";
  }
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.9);
  const [licenseBeat, setLicenseBeat] = useState(null);
  const [freeBeat, setFreeBeat] = useState(null);

  const current = index >= 0 && index < queue.length ? queue[index] : null;

  useEffect(() => {
    const a = audioRef.current;
    const onTime = () => setProgress(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => {
      setPlaying(false);
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const loadAndPlay = (beat) => {
    const a = audioRef.current;
    a.src = fileUrl(beat.audio_url);
    a.volume = volume;
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  const playBeat = (beat, list) => {
    const q = list && list.length ? list : [beat];
    const i = q.findIndex((b) => b.id === beat.id);
    setQueue(q);
    setIndex(i >= 0 ? i : 0);
    setProgress(0);
    loadAndPlay(beat);
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!current) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const next = () => {
    if (queue.length < 2) return;
    const ni = (index + 1) % queue.length;
    setIndex(ni);
    setProgress(0);
    loadAndPlay(queue[ni]);
  };

  const prev = () => {
    if (queue.length < 2) return;
    const pi = (index - 1 + queue.length) % queue.length;
    setIndex(pi);
    setProgress(0);
    loadAndPlay(queue[pi]);
  };

  const seek = (t) => {
    const a = audioRef.current;
    a.currentTime = t;
    setProgress(t);
  };

  const setVolume = (v) => {
    audioRef.current.volume = v;
    setVolumeState(v);
  };

  const value = useMemo(
    () => ({
      queue, index, current, playing, progress, duration, volume,
      playBeat, toggle, next, prev, seek, setVolume,
      isCurrent: (beat) => current?.id === beat?.id,
      openLicense: setLicenseBeat,
      licenseBeat,
      closeLicense: () => setLicenseBeat(null),
      openFreeDownload: setFreeBeat,
      freeBeat,
      closeFreeDownload: () => setFreeBeat(null),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queue, index, playing, progress, duration, volume, licenseBeat, freeBeat]
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}
